import { Fragment } from 'react'
import { Link } from '@inertiajs/react'
import { Box, Loader } from '@mantine/core'
import { IconChevronLeft, IconChevronRight, IconReload } from '@tabler/icons-react'
import { useTranslation } from 'react-i18next'
import { cx } from '../../utils/cx'
import css from '../../styles/EditorV2.module.css'
import classes from './StepHeader.module.css'

const STEPS = ['editAd', 'preview', 'delivery', 'targeting']

/**
 * The booking stepper bar of the approved design (Figma "header", second row). Only "Edit
 * Ad" exists in this editor: Preview, Delivery and Targeting are drawn as upcoming steps but
 * are not links yet. "Refresh Ad" replays the live preview; "Continue" validates and saves.
 * AD-259: the design team's steps end with the design (and its landing page): no booking steps,
 * and `continueLabel` names their "Approve / Publish".
 */
export function StepHeader({
  homeUrl, onRefresh, onContinue, continueDisabled, saving, withLandingpage, actions = null,
  continueLabel = null, designSteps = false,
}) {
  const { t } = useTranslation()
  const stepKeys = [
    STEPS[0],
    ...(withLandingpage ? ['landingpage'] : []),
    ...(designSteps ? [] : STEPS.slice(1)),
  ]
  const label = continueLabel || t('editorV2.shell.continue')
  const steps = stepKeys.map((key) => t(`editorV2.shell.steps.${key}`))

  return (
    <div className={classes.bar}>
      <div className={classes.left}>
        <Link href={homeUrl} className={cx(css.button, classes.compactButton)} aria-label={t('editorV2.shell.backToInspiration')}>
          <IconChevronLeft size={14} stroke={1.8} aria-hidden="true" />
          <span className={classes.long}>{t('editorV2.shell.backToInspiration')}</span>
        </Link>
      </div>

      <ol className={classes.steps} aria-label={t('editorV2.shell.steps.label')}>
        {steps.map((label, index) => (
          <Fragment key={label}>
            {index > 0 && <li className={classes.divider} aria-hidden="true" />}
            <li
              className={classes.step}
              data-active={index === 0 ? 'true' : 'false'}
              aria-current={index === 0 ? 'step' : undefined}
            >
              <span className={classes.number}>{index + 1}</span>
              <span>{label}</span>
            </li>
          </Fragment>
        ))}
      </ol>

      <Box component="p" hiddenFrom="sm" className={classes.count}>
        <b>{t('editorV2.shell.steps.count', { current: 1, total: steps.length })}</b>
        <span>{steps[0]}</span>
      </Box>

      <div className={classes.right}>
        {actions}
        <button type="button" className={cx(css.button, classes.compactButton, classes.refresh)} onClick={onRefresh} aria-label={t('editorV2.shell.refreshAd')}>
          <IconReload size={16} stroke={1.6} aria-hidden="true" />
          <span className={classes.long}>{t('editorV2.shell.refreshAd')}</span>
        </button>
        <button
          type="button"
          className={cx(css.button, css.buttonPrimary, classes.continue)}
          onClick={onContinue}
          disabled={continueDisabled}
          aria-label={label}
        >
          {saving ? <Loader size={14} color="white" /> : null}
          <span className={classes.continueLabel}>{label}</span>
          {!saving && <IconChevronRight size={16} stroke={1.8} aria-hidden="true" />}
        </button>
      </div>
    </div>
  )
}
